import React, { useState } from 'react';
import { HiChevronUp, HiChevronDown, HiTrash, HiPencil } from 'react-icons/hi';

const BLOCK_TYPES = [
  { type: 'text', label: 'Text' },
  { type: 'hero_slider', label: 'Hero Slider' },
  { type: 'image', label: 'Image Full-Width' },
  { type: 'image_duo', label: 'Grid 2 Images' },
  { type: 'video', label: 'Video' },
  { type: 'carousel', label: 'Carousel' },
  { type: 'gallery', label: 'Gallery' },
  { type: 'stats', label: 'Stats' },
  { type: 'products', label: 'Products Grid' },
  { type: 'divider', label: 'Divider' },
  { type: 'separator', label: 'Separator' },
  { type: 'pullquote', label: 'Pull Quote' },
  { type: 'related_posts', label: 'Related Posts' },
];

const DEFAULT_DATA = {
  text: { heading: '', body: '', pullQuote: null },
  hero_slider: { slides: [{ image: '', alt: '', tag: '', title: '', description: '', cta: '', ctaLink: '' }], autoplay: false, autoplayDelay: 5000 },
  image: { image: '', alt: '', height: '600px', overlay: 'none' },
  image_duo: { image1: '', image2: '', caption1: '', caption2: '', ratio: 'portrait' },
  video: { url: '', thumbnail: '', caption: '', type: 'YouTube', autoplay: false },
  carousel: { images: [], visibleItems: 2, showCaptions: true },
  gallery: { images: [] },
  stats: { stats: [{ number: '', label: '' }, { number: '', label: '' }, { number: '', label: '' }] },
  products: { title: '', products: [], columns: 2 },
  divider: { style: 'line' },
  separator: { style: 'line', symbol: '' },
  pullquote: { quote: '', attribution: '', style: 'border-left' },
  related_posts: { title: 'Continúa explorando', description: '', postSlugs: [], layout: 2 },
};

function getBlockPreview(block) {
  switch (block.type) {
    case 'text': return block.data.heading || block.data.body?.slice(0, 50) || 'Empty text block';
    case 'hero_slider': return `${block.data.slides?.length || 0} slides`;
    case 'video': return block.data.caption || block.data.url || 'No video';
    case 'carousel': return `${block.data.images?.length || 0} images`;
    case 'gallery': return `${block.data.images?.length || 0} images`;
    case 'stats': return block.data.stats?.map(s => s.number).filter(Boolean).join(', ') || 'No stats';
    case 'divider': return block.data.style || 'line';
    case 'pullquote': return block.data.quote?.slice(0, 50) || 'Empty quote';
    case 'image': return block.data.alt || block.data.image?.split('/').pop() || 'No image';
    case 'image_duo': return [block.data.image1, block.data.image2].filter(Boolean).length + ' images';
    case 'products': return `${block.data.products?.length || 0} products`;
    case 'separator': return block.data.style || 'line';
    case 'related_posts': return `${block.data.postSlugs?.length || 0} posts`;
    default: return '';
  }
}

export default function BlockEditor({ blocks, selectedBlockId, onSelectBlock, onUpdateBlocks }) {
  const [showTypeSelector, setShowTypeSelector] = useState(false);

  const addBlock = (type) => {
    const newBlock = {
      id: 'block_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      type,
      order: blocks.length,
      data: { ...DEFAULT_DATA[type] },
    };
    onUpdateBlocks([...blocks, newBlock]);
    onSelectBlock(newBlock.id);
    setShowTypeSelector(false);
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newBlocks.length) return;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    newBlocks.forEach((b, i) => { b.order = i; });
    onUpdateBlocks(newBlocks);
  };

  const deleteBlock = (id) => {
    const newBlocks = blocks.filter(b => b.id !== id);
    newBlocks.forEach((b, i) => { b.order = i; });
    onUpdateBlocks(newBlocks);
    if (selectedBlockId === id) onSelectBlock(null);
  };

  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order);

  return (
    <div className="adminblog-block-list">
      <div className="adminblog-block-list-header">
        <h3>Content Blocks</h3>
        <span style={{ fontSize: 12, color: '#999' }}>{blocks.length} blocks</span>
      </div>

      {sortedBlocks.map((block, index) => (
        <div
          key={block.id}
          className={`adminblog-block-item ${selectedBlockId === block.id ? 'selected' : ''}`}
          onClick={() => onSelectBlock(block.id)}
        >
          <div className="adminblog-block-handle">
            <span style={{ fontSize: 10 }}>⋮⋮</span>
          </div>
          <span className="adminblog-block-type">{block.type.replace('_', ' ')}</span>
          <span className="adminblog-block-preview-text">{getBlockPreview(block)}</span>
          <div className="adminblog-block-actions">
            <button
              className="adminblog-block-action"
              onClick={(e) => { e.stopPropagation(); moveBlock(index, -1); }}
              disabled={index === 0}
              title="Move up"
            >
              <HiChevronUp />
            </button>
            <button
              className="adminblog-block-action"
              onClick={(e) => { e.stopPropagation(); moveBlock(index, 1); }}
              disabled={index === sortedBlocks.length - 1}
              title="Move down"
            >
              <HiChevronDown />
            </button>
            <button
              className="adminblog-block-action"
              onClick={(e) => { e.stopPropagation(); onSelectBlock(block.id); }}
              title="Edit"
            >
              <HiPencil />
            </button>
            <button
              className="adminblog-block-action delete"
              onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
              title="Delete"
            >
              <HiTrash />
            </button>
          </div>
        </div>
      ))}

      <div className="adminblog-add-block-area">
        {showTypeSelector && (
          <div className="adminblog-block-type-selector">
            {BLOCK_TYPES.map(bt => (
              <button
                key={bt.type}
                className="adminblog-block-type-option"
                onClick={() => addBlock(bt.type)}
              >
                {bt.label}
              </button>
            ))}
          </div>
        )}
        <button
          className="adminblog-add-block-btn"
          onClick={() => setShowTypeSelector(!showTypeSelector)}
        >
          {showTypeSelector ? '✕ Cancel' : '+ Add Block'}
        </button>
      </div>
    </div>
  );
}
